import React, { useState, useEffect, useRef, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './lib/firebase';
import './index.css';
import { useAuth } from './hooks/useAuth';
import { useSettings } from './hooks/useSettings';
import { useBoards } from './hooks/useData';
import { useUserPrefs } from './hooks/useUserPrefs';
import { consumePendingConsent } from './lib/termsVersion';
import { useFirestoreSyncMonitor } from './hooks/useFirestoreSyncMonitor';
import { useReminders } from './hooks/useReminders';
import { usePushSubscription } from './hooks/usePushSubscription';
import { useListBoardMap } from './hooks/useListBoardMap';
import { useResolvedBoard } from './hooks/useResolvedBoard';
import { useSharedWithMe } from './hooks/useSharedWithMe';
import { useSharedListPlacements } from './hooks/useSharedListPlacements';
import useSignupsOpen from './hooks/useSignupsOpen';
import WaitlistScreen from './components/WaitlistScreen';
import LoginPage from './pages/LoginPage';
import BoardPage from './pages/BoardPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import SharedWithMePage from './pages/SharedWithMePage';
import ListPage from './pages/ListPage';
import RemindersEditPage from './pages/RemindersEditPage';
import TrashPage from './pages/TrashPage';
import SharePage from './pages/SharePage';
import RecoverAccountPage from './pages/RecoverAccountPage';
import GoodbyePage from './pages/GoodbyePage';
import MarketingPage from './pages/MarketingPage';
import ContactPage from './pages/ContactPage';
import WelcomeSetup from './components/WelcomeSetup';
import NewBoardModal from './components/NewBoardModal';
import InstallHint from './components/InstallHint';
import ExtensionBlockHint from './components/ExtensionBlockHint';
import NewVersionHint from './components/NewVersionHint';
import StaleConnectionBanner from './components/StaleConnectionBanner';
import AppErrorBoundary from './components/AppErrorBoundary';
import GuestUpgradeHint from './components/GuestUpgradeHint';
import { TEMPLATE_BLANK, stripTemplateContent } from './lib/boardTemplates';

// Admin console is bundle-split — only fetched when /admin is hit.
// Regular users never download the admin code (cosmetic, not a
// security boundary; the role check + Cloud Function gate are the
// real protection — see ADMIN_SPEC.md §6 "Bundle leakage").
const AdminApp = React.lazy(() => import('./pages/AdminApp'));

// Editor lab — dev-only, unlinked sandbox for the CodeMirror MarkdownEditor
// exploration (Walter 2026-07-06). Lazy so CodeMirror lands in its OWN chunk
// and never loads on the main path until we flip the editor flag for real.
const EditorLab = React.lazy(() => import('./pages/EditorLab'));

// SessionStorage flag for the once-per-session lastVisitAt touch.
// Stays for the lifetime of the tab; a refresh writes once, then
// no more writes that session. New tab → new session → another
// write. Good enough for the "visits in last 30 days" metric.
const LAST_VISIT_TOUCHED_KEY = 'lists_last_visit_touched_session';

function AppInner() {
  // Walter 2026-05-13: call useSettings here at the app root so the
  // theme (light by default) is applied to document.documentElement
  // on EVERY page — LoginPage, marketing pages, contact page,
  // WelcomeSetup, all of them. Previously only BoardPage/MobileBoard
  // called useSettings, so anyone not yet inside the app saw the bare
  // :root defaults (which are dark) regardless of the new light
  // default. The hook's only render-affecting state is the
  // data-theme attribute it writes via useEffect — the returned
  // values are duplicated in BoardPage/MobileBoard's own useSettings
  // calls (idempotent — same localStorage read).
  useSettings();
  const {
    user,
    magicLinkProcessing,
    signInWithGoogle,
    signInWithGoogleIdToken,
    signInAsGuest,
    sendMagicLink,
    linkToGoogle,
    linkToMagicLink,
    signOut,
  } = useAuth();
  // Walter 2026-06-06: new-sign-ups safety switch. When closed, the LOGIN
  // page still shows normally so existing users (and admins) can sign in —
  // only the "try without an account" guest slot is swapped for a "join the
  // waitlist" action. The Turnstile waitlist screen shows on demand (the
  // LoginPage button, or the landing CTA which links to #waitlist).
  // Fail-open (signupsOpen defaults true; only an explicit false pauses).
  const signupsOpen = useSignupsOpen();
  const [showWaitlist, setShowWaitlist] = useState(false);
  const { boards, loading: boardsLoading, addBoard, addBoardFromTemplate, updateBoard, deleteBoard } = useBoards(user?.uid);
  const { prefs: userPrefs, updatePrefs: updateUserPrefs } = useUserPrefs(user?.uid);
  // Walter 2026-05-16: multi-device sync hardening. (1) Bounces the
  // Firestore network on visibility-change so iOS/Android PWAs catch
  // up in seconds (was 10+ minutes) when foregrounded after being
  // suspended. (2) Tracks listener fromCache state so SaveIndicator
  // can show a "reconnecting" variant during the brief stale window.
  // See src/hooks/useFirestoreSyncMonitor.js.
  useFirestoreSyncMonitor(user?.uid);
  // useReminders subscribes to all the user's reminder docs across all
  // boards. Lives at app root so any surface (capture flow in panels,
  // agenda view in header) can read or write without re-subscribing.
  //
  // Bug #12 (shared reminder sync) note: useReminders runs a
  // collectionGroup query gated by `aclUids`. Legacy docs without
  // aclUids get backfilled at subscription time with `[currentUid]`
  // — owner-visible immediately, collaborator-visible only after
  // the next edit. A smarter backfill would pass `listsForAcl` here
  // so the source list's editors/viewers are baked in too, but the
  // user's full list set isn't subscribed at App level (it's per-
  // board). Documented v1 limitation; see shared-reminder-sync.md.
  const { createReminder, updateReminder, deleteReminder, reminders } = useReminders(user?.uid);
  // Push subscription manager — owns the OS-level permission state
  // and the Firestore push-subscription doc for THIS browser/device.
  // Settings UI uses it to flip the push toggle into a real permission
  // request flow.
  const pushSub = usePushSubscription(user?.uid);
  // Resolves listId → boardId across the user's boards. Used by the
  // agenda jump and the board-dropdown "has reminders" markers to
  // recover boardId for older reminders that pre-date sourceBoardId.
  const { map: listIdToBoardId } = useListBoardMap(user?.uid, boards);
  // "Shared with me" entries — boards / lists other users have shared
  // with the current user. The Sidebar renders these in a dedicated
  // section so the user doesn't need to keep share URLs as bookmarks.
  const { items: sharedWithMe, loading: sharedWithMeLoading } = useSharedWithMe(user?.uid);
  // Walter 2026-05-11: per-user placement state for shared lists.
  // Each entry maps a shared list to one of the user's own boards,
  // so the list renders inline with their own lists on that board.
  // Sidebar filters placed lists out of "shared with me" so the
  // section stays as just unplaced/orphan shares + shared boards.
  const {
    placements: sharedListPlacements,
    placementsByBoard: sharedListPlacementsByBoard,
    setPlacement: setSharedListPlacement,
    clearPlacement: clearSharedListPlacement,
  } = useSharedListPlacements(user?.uid);
  const navigate = useNavigate();
  const location = useLocation();
  // The /accept-invite/:token route is special: it must render BEFORE
  // the !user → LoginPage gate, because the accept-invite page itself
  // owns the sign-in flow (Google / magic-link inline). If we let the
  // LoginPage gate fire first, the user signs in and then loses the
  // token — they land on /board/... with no record of the pending
  // invite. So we route through AcceptInvitePage early.
  const isAcceptInvite = location.pathname.startsWith('/accept-invite/');
  // Same special-case as /accept-invite: the reminder-edit page
  // needs to render even when !user so it can show its own sign-in
  // widget (with reminder context, not the generic LoginPage).
  // After sign-in the page re-renders with user set and loads the
  // reminder.
  const isReminderEdit = /^\/reminders\/[^/]+\/edit$/.test(location.pathname);
  // Public-link share landing page — same early-return pattern. Page
  // shows its own sign-in widget so a logged-out user clicking from
  // anywhere can join in one round-trip.
  const isShareLink = location.pathname.startsWith('/share/');
  // Self-deletion recovery — token in URL is the auth proof; the
  // page calls selfCancelDeletion + signInWithCustomToken on mount,
  // so it must render even when !user.
  const isRecoverAccount = /^\/recover-account\/[^/]+$/.test(location.pathname);
  // Static post-suspend page; no auth needed.
  const isGoodbye = location.pathname === '/goodbye';
  // Dev-only editor sandbox; renders the flagged CodeMirror editor in the
  // real build. Unlinked — reachable only by typing /editor-lab.
  const isEditorLab = location.pathname === '/editor-lab';

  // Once-per-session lastVisitAt touch. Writes a single field on the
  // user's account doc to record activity for the admin metrics card
  // (ADMIN_SPEC.md §3.2). Gated by a sessionStorage flag so we touch
  // exactly once per tab-load — Firestore writes have a non-zero
  // cost, and we only need calendar-day-granular accuracy for the
  // "visits in last 30 days" metric.
  useEffect(() => {
    if (!user?.uid || user.isAnonymous) return;
    let cancelled = false;
    try {
      if (sessionStorage.getItem(LAST_VISIT_TOUCHED_KEY)) return;
    } catch {} // sessionStorage can throw in some embedded contexts
    (async () => {
      try {
        await setDoc(
          doc(db, 'users', user.uid, 'account', 'main'),
          { lastVisitAt: serverTimestamp() },
          { merge: true },
        );
        if (cancelled) return;
        try { sessionStorage.setItem(LAST_VISIT_TOUCHED_KEY, '1'); } catch {}
      } catch (err) {
        // Non-fatal — the metric just doesn't update for this
        // session. Most likely cause is offline; the next session
        // will write and catch up.
        console.warn('[App] lastVisitAt touch failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid, user?.isAnonymous]);

  // Auto-detect and persist the user's IANA timezone so the reminder
  // fire engine can compute next-occurrences in the user's local time
  // (DST-aware). Only writes when (a) we have prefs loaded, (b) the
  // current detected tz isn't what's already stored. Cheap one-time
  // write per device-or-travel-change. Walter 2026-05-10.
  useEffect(() => {
    if (!user?.uid || !userPrefs) return;
    let detected = null;
    try {
      detected = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch (e) { /* old/sandboxed runtime — leave null, server falls back to UTC */ }
    if (detected && detected !== userPrefs.timezone) {
      updateUserPrefs({ timezone: detected }).catch(err => {
        console.warn('[App] timezone update failed', err);
      });
    }
    // Watch only the timezone field — userPrefs is a fresh object on
    // every snapshot, and we don't want to re-fire on unrelated pref
    // updates (e.g. flipping a notification toggle).
  }, [user?.uid, userPrefs?.timezone, updateUserPrefs]);

  // Walter 2026-05-15: flush click-accept Terms consent.
  // LoginPage stashes a pending-consent record in localStorage when the
  // user checks the agreement box and clicks any sign-in button — that
  // happens BEFORE Firebase Auth completes (especially for Google OAuth
  // redirect + magic-link round-trip). Once user.uid + userPrefs are
  // in hand, we write it to Firestore and clear the localStorage flag.
  // Runs at most once per sign-in (consumePendingConsent is destructive).
  useEffect(() => {
    if (!user?.uid || !userPrefs) return;
    const pending = consumePendingConsent();
    if (!pending) return;
    updateUserPrefs({
      termsAcceptedVersion: pending.version,
      termsAcceptedAt: pending.at,
    }).catch(err => {
      console.warn('[App] terms-consent flush failed', err);
    });
  }, [user?.uid, userPrefs, updateUserPrefs]);

  // Paths that name a specific resource the user explicitly asked for.
  // The welcome-setup popup is wrong by definition on any of these:
  // the user typed (or was redirected to) `/list/:id`, `/board/:id`,
  // or `/shared-with-me` — they want THAT thing, not a template
  // picker. Critically, an invitee accepting a per-list share lands
  // on `/list/:id` from acceptShareInvite's redirect; without this
  // bypass, the welcome popup races the sharedWithMe pointer doc's
  // local-cache propagation and frequently wins, dropping the user
  // into 3 default boards instead of the list they were invited to.
  // useSharedWithMe's loading flag goes false on the FIRST snapshot,
  // which (for a fresh sign-in) is the empty cache snapshot — so the
  // gate the welcome useEffect uses to wait for shared content is
  // moot. Path-based suppression is deterministic and correct.
  const pathDeclaresIntent =
    isAcceptInvite
    || isReminderEdit
    || isShareLink
    || isRecoverAccount
    || isGoodbye
    || location.pathname.startsWith('/list/')
    || location.pathname.startsWith('/board/')
    || location.pathname.startsWith('/shared-with-me');

  // Welcome-setup popup is shown the first time a user has zero boards.
  // We track "has the popup been shown / dismissed for this session" with a
  // ref so we don't keep firing it as state churns.
  const [showWelcome, setShowWelcome] = useState(false);
  const welcomeHandledRef = useRef(false);
  // New-board modal (replaces the old window.prompt flow).
  const [showNewBoard, setShowNewBoard] = useState(false);

  useEffect(() => {
    // Wait for BOTH boards and sharedWithMe to finish their initial
    // subscription. Without this gate, a fresh share-recipient lands
    // on the app, sees boards.length=0 (correct) and an empty
    // sharedWithMe (still loading), so welcome fires and creates
    // template boards before the share pointer doc has been read.
    // The user ends up looking like a brand-new user with no shared
    // content — which is exactly what we want to avoid.
    //
    // Also bail when the URL declares specific intent — see the
    // `pathDeclaresIntent` comment above.
    if (!user || boardsLoading || sharedWithMeLoading || pathDeclaresIntent) return;
    const hasSharedContent = Array.isArray(sharedWithMe) && sharedWithMe.length > 0;
    if (boards.length > 0 || hasSharedContent || welcomeHandledRef.current) return;
    // Walter 2026-05-12: belt-and-suspenders for the cleared-cache
    // sign-in race. A returning Google user who clears cache and
    // signs back in can hit a window where Firestore's FIRST snapshot
    // is from the (empty) local cache before the server snapshot
    // lands — boardsLoading flips to false with boards=[], welcome
    // mis-fires, and the user ends up with template boards added on
    // top of their real ones once the server snapshot arrives.
    // If the auth account is more than a few minutes old, this is
    // by definition NOT a first-time user, regardless of what the
    // Firestore snapshot says. Auto-seed a blank board (same shape
    // as the welcomeShown=true branch below) and return.
    const FIRST_SIGNIN_WINDOW_MS = 5 * 60 * 1000;
    const creationMs = user.metadata?.creationTime
      ? new Date(user.metadata.creationTime).getTime()
      : 0;
    if (creationMs && Date.now() - creationMs > FIRST_SIGNIN_WINDOW_MS) {
      // Skip welcome entirely. We DON'T auto-seed here, because if
      // this branch fires due to a cache-miss snapshot race (rather
      // than a legitimately-empty returning user), seeding a "not
      // named yet" board adds an unwanted phantom alongside the real
      // boards that arrive in the next snapshot. The very rare
      // legit-empty case (user deleted all their boards) lands on
      // an empty board state with the "+ new board" affordance,
      // which is the right next-step UI anyway.
      welcomeHandledRef.current = true;
      return;
    }
    welcomeHandledRef.current = true;
    // Returning-user gate: a user who has already completed welcome
    // (account.welcomeShown=true) shouldn't see the popup again, even
    // if they've since deleted all their boards or been admin-restored
    // from a self-suspend with no remaining content. We async-read
    // the account doc and either fire the popup (first-timer) or auto-
    // seed a single blank board (returning user with no boards).
    (async () => {
      try {
        const accountSnap = await getDoc(doc(db, 'users', user.uid, 'account', 'main'));
        const welcomeShown = accountSnap.exists() && accountSnap.data().welcomeShown === true;
        if (welcomeShown) {
          // Returning user — silently seed one blank board. Same UX as
          // the invitee soft-seed effect below, just without sharedWithMe
          // as the trigger.
          try {
            await addBoard('not named yet');
          } catch (e) {
            console.warn('[App] auto-seed for returning user failed', e);
          }
          return;
        }
        setShowWelcome(true);
      } catch (err) {
        console.warn('[App] welcomeShown probe failed; falling back to popup', err);
        setShowWelcome(true);
      }
    })();
  }, [user, boards, boardsLoading, sharedWithMe, sharedWithMeLoading, pathDeclaresIntent, addBoard]);

  // Soft-blank-board seed for invitees. When a user lands with NO own
  // boards but DOES have shared content (i.e. they came in via an
  // accepted share), auto-create a single empty board so the app
  // isn't completely empty if they want to jot something themselves.
  // The welcome popup is suppressed for invitees (see
  // `pathDeclaresIntent` above), so without this nudge their dropdown
  // would only contain "shared with me" and no place of their own.
  //
  // Per-mount session ref so a single mount doesn't fire the seed
  // twice if deps churn. Reset on failure to allow retry. Name is
  // 'not named yet' to match the blank-template default — same
  // copy the new-board picker uses, same nudge to rename.
  //
  // Edge case: a user who later deletes all their own boards while
  // still having shared-with-me entries will re-trigger the seed
  // on the next page load. Acceptable for now — they get one blank
  // back, can delete it again if undesired. If this becomes
  // surprising in practice, gate on an `account.autoBlankSeeded`
  // flag so it fires at most once per uid lifetime.
  const blankBoardSeededRef = useRef(false);
  useEffect(() => {
    if (!user) return;
    if (boardsLoading || sharedWithMeLoading) return;
    if (blankBoardSeededRef.current) return;
    if (boards.length > 0) return;
    if (!Array.isArray(sharedWithMe) || sharedWithMe.length === 0) return;
    // Walter 2026-05-11: don't soft-seed while the accept-invite flow
    // is in progress. The BoardPickerModal there guides the user to
    // create their own first board (with a meaningful name) at the
    // moment of placement. Without this guard, the soft-seed fires
    // in parallel — a "not named yet" phantom board shows up
    // alongside the board the user actually chose / created via the
    // picker. The check is purely path-based: any URL other than
    // /accept-invite/* runs the seed as before.
    if (isAcceptInvite) return;
    blankBoardSeededRef.current = true;
    addBoard('not named yet').catch(err => {
      console.warn('[App] auto-seed blank board failed', err);
      blankBoardSeededRef.current = false;
    });
  }, [user, boards, boardsLoading, sharedWithMe, sharedWithMeLoading, addBoard, isAcceptInvite]);

  const handleSwitchBoard = (id) => navigate(`/board/${id}`);

  // Welcome-setup confirm: receives the array of selected templates in
  // display order (organizer-first). Creates each, then navigates to the
  // most-structured one available — organizer if checked, else simple,
  // else blank. Falls back to creating a single blank board if somehow
  // nothing was selected (the popup blocks this but defensive).
  const handleWelcomeConfirm = async (selectedTemplates) => {
    const list = selectedTemplates.length > 0
      ? selectedTemplates
      : [{ id: 'blank', defaultBoardName: 'main', mode: 'free', lists: [] }];

    // Create them in order. We could batch, but template creates already
    // use a Firestore batch internally — sequencing here keeps Firestore
    // doc createdAt timestamps strictly ordered, which the sidebar order
    // depends on.
    const created = [];
    // Aggregate any list IDs the templates flagged as designated reminder
    // lists (e.g. the GTD/Organizer template seeds a "Reminders" list).
    // We promote them in userPrefs once after all boards are created so
    // a single Firestore write captures the whole batch.
    // Walter 2026-05-26 — same shape now for designated LOG lists too.
    const allDesignatedIds = [];
    const allDesignatedLogIds = [];
    for (const tpl of list) {
      const { boardId, designatedListIds, designatedLogListIds } =
        await addBoardFromTemplate(tpl, tpl.defaultBoardName);
      created.push({ id: boardId, tpl });
      if (Array.isArray(designatedListIds) && designatedListIds.length > 0) {
        allDesignatedIds.push(...designatedListIds);
      }
      if (Array.isArray(designatedLogListIds) && designatedLogListIds.length > 0) {
        allDesignatedLogIds.push(...designatedLogListIds);
      }
    }
    // One write covering both designations (if either has work to do)
    // so we don't churn userPrefs with two separate updates.
    if (allDesignatedIds.length > 0 || allDesignatedLogIds.length > 0) {
      const prefsPatch = {};
      if (allDesignatedIds.length > 0) {
        const existing = Array.isArray(userPrefs?.designatedReminderListIds)
          ? userPrefs.designatedReminderListIds
          : (userPrefs?.designatedReminderListId
              ? [userPrefs.designatedReminderListId]
              : []);
        prefsPatch.designatedReminderListIds = Array.from(new Set([...existing, ...allDesignatedIds]));
      }
      if (allDesignatedLogIds.length > 0) {
        const existingLog = Array.isArray(userPrefs?.designatedLogListIds)
          ? userPrefs.designatedLogListIds
          : [];
        prefsPatch.designatedLogListIds = Array.from(new Set([...existingLog, ...allDesignatedLogIds]));
      }
      await updateUserPrefs(prefsPatch);
    }

    // Mark welcome as completed so future sign-ins (e.g. after a
    // self-delete + admin-restore round-trip, or a user who later
    // deletes all their boards) don't re-fire the popup.
    try {
      await setDoc(
        doc(db, 'users', user.uid, 'account', 'main'),
        { welcomeShown: true },
        { merge: true },
      );
    } catch (err) {
      console.warn('[App] failed to mark welcomeShown', err);
    }

    // Walter 2026-06-09: land on the blank "my first board" so new users
    // see an empty, approachable board (with a clear first-step prompt)
    // instead of the dense organizer. Organizer is still created, one
    // click away in the board switcher.
    const priority = ['blank', 'simple', 'organizer'];
    const target =
      priority
        .map(pid => created.find(c => c.tpl.id === pid))
        .find(Boolean) || created[0];

    setShowWelcome(false);
    // FULL PAGE LOAD instead of an SPA navigate. The SPA path has a
    // race where BoardRoute mounts on the new URL before the boards
    // onSnapshot listener has fired with the just-created docs in
    // local state; useResolvedBoard then runs a cross-user lookup,
    // fails (the user isn't in their own editors/viewers — they're
    // the owner), and BoardRoute bounces to "/", which also shows
    // empty-state loading until the listener finally catches up.
    // A full reload boots clean: the writes are persisted in
    // Firestore's local IndexedDB cache, so the post-reload boards
    // listener fires near-instantly with the new docs, and
    // BoardRoute renders BoardPage on first try. Same trick
    // AcceptInvitePage uses for its post-signin redirect.
    if (target) window.location.href = `/board/${target.id}`;
  };

  // Triggered by the "+ new board" entry in the BoardPage dropdown.
  const handleNewBoard = () => {
    setShowNewBoard(true);
  };

  // Walter 2026-05-11: inline blank-board create used by the
  // BoardPickerModal (sharing-refinements). Skips the new-board modal
  // entirely and returns the new board id so the picker can write its
  // sharedListPlacements doc against it on the same tick. No
  // navigation — the picker decides where to send the user (typically
  // /board/{newId} via a full reload).
  const handleCreateBlankBoard = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return null;
    const seed = stripTemplateContent(TEMPLATE_BLANK);
    const res = await addBoardFromTemplate(seed, trimmed);
    return res?.boardId || null;
  };

  // Confirm from the NewBoardModal. Creates the board (with template seed
  // lists if applicable), navigates to it, closes the modal.
  // Walter 2026-05-26 — handles BOTH designated-reminder and designated-
  // log lists from the template (added the log path here so a new
  // organizer board gets its Log list's auto-stamp behaviour activated;
  // mirrors handleWelcomeConfirm's same dual-promotion).
  const handleNewBoardCreate = async (template, customName) => {
    const { boardId, designatedListIds, designatedLogListIds } =
      await addBoardFromTemplate(template, customName);
    const prefsPatch = {};
    if (Array.isArray(designatedListIds) && designatedListIds.length > 0) {
      const existing = Array.isArray(userPrefs?.designatedReminderListIds)
        ? userPrefs.designatedReminderListIds
        : (userPrefs?.designatedReminderListId
            ? [userPrefs.designatedReminderListId]
            : []);
      prefsPatch.designatedReminderListIds = Array.from(new Set([...existing, ...designatedListIds]));
    }
    if (Array.isArray(designatedLogListIds) && designatedLogListIds.length > 0) {
      const existingLog = Array.isArray(userPrefs?.designatedLogListIds)
        ? userPrefs.designatedLogListIds
        : [];
      prefsPatch.designatedLogListIds = Array.from(new Set([...existingLog, ...designatedLogListIds]));
    }
    if (Object.keys(prefsPatch).length > 0) {
      await updateUserPrefs(prefsPatch);
    }
    setShowNewBoard(false);
    navigate(`/board/${boardId}`);
  };

  const handleRenameBoard = (id, name) => updateBoard(id, { name });
  // Generic patch for anything on the board doc — used by BoardPage to flip
  // free↔grid mode, set the grid column count, and stash/clear the free-mode
  // snapshot captured at transition time.
  const handleUpdateBoard = (id, patch) => updateBoard(id, patch);

  const handleDeleteBoard = (id) => {
    deleteBoard(id);
    const remaining = boards.filter(b => b.id !== id);
    if (remaining.length > 0) navigate(`/board/${remaining[0].id}`);
  };

  // Magic-link return URLs land here. While useAuth completes the sign-in,
  // show a splash so the user doesn't see the login page flicker behind it.
  if (magicLinkProcessing) return <Loading text="signing you in…" />;
  if (user === undefined) return <Loading text="loading..." />;

  // Accept-invite path renders its own page regardless of sign-in state.
  // The page handles sign-in inline, then fires acceptShareInvite and
  // navigates to the board.
  if (isAcceptInvite) {
    return (
      <Routes>
        <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      </Routes>
    );
  }
  // Reminder-edit path — same special case (renders own sign-in
  // widget with reminder context, then loads the reminder).
  if (isReminderEdit) {
    return (
      <Routes>
        <Route path="/reminders/:reminderId/edit" element={<RemindersEditPage />} />
      </Routes>
    );
  }
  // Public-link share path — renders own sign-in widget then auto-
  // calls joinViaShareToken once user is set.
  if (isShareLink) {
    return (
      <Routes>
        <Route path="/share/:token" element={<SharePage />} />
      </Routes>
    );
  }
  // Self-deletion recovery — fires selfCancelDeletion + signs the
  // user in via custom token. Must render before the !user → Login
  // gate so the recovery flow can complete from a signed-out state.
  if (isRecoverAccount) {
    return (
      <Routes>
        <Route path="/recover-account/:token" element={<RecoverAccountPage />} />
      </Routes>
    );
  }
  // Static goodbye page after self-suspend. User is signed out by
  // this point; just confirms the deletion is scheduled.
  if (isGoodbye) {
    return (
      <Routes>
        <Route path="/goodbye" element={<GoodbyePage />} />
      </Routes>
    );
  }
  // Dev-only editor sandbox. Suspense while the CodeMirror chunk loads.
  if (isEditorLab) {
    return (
      <Suspense fallback={<Loading text="loading editor…" />}>
        <Routes>
          <Route path="/editor-lab" element={<EditorLab />} />
        </Routes>
      </Suspense>
    );
  }

  // Marketing / legal pages — public, no auth required. Walter
  // 2026-05-09: published before public traffic exists so we can see
  // the docs render in real chrome before the lawyer-review pass.
  // Each route renders MarketingPage with a slug pointing at the
  // corresponding .md file in /public/marketing/.
  const marketingMatch = location.pathname.match(/^\/(landing|about|help|privacy|terms)\/?$/);
  if (marketingMatch) {
    const slug = marketingMatch[1];
    // Walter 2026-06-16: hasSession is true for ANY user incl. an anonymous
    // GUEST — they already have a board, so the marketing-page CTA should be
    // "back to your board", not "try listicity". isAuthed (signed-in only)
    // still drives the "Open your boards" copy.
    return <MarketingPage slug={slug} isAuthed={!!user && !user.isAnonymous} hasSession={!!user} />;
  }
  // Contact page — public, like the marketing pages above. Renders its
  // own chrome (matching MarketingPage's) with ContactForm in the body.
  // Walter 2026-05-10: replaces the mailto:listicityapp@gmail.com link
  // that used to live in the marketing footer.
  if (/^\/contact\/?$/.test(location.pathname)) {
    return <ContactPage user={user} />;
  }

  if (!user) {
    // Walter 2026-05-13: signed-out users at the bare root see the
    // marketing landing page instead of the login form. They get a
    // friendlier first impression + the same content that anyone who
    // visits /landing directly would see. The landing's CTA points
    // at /board, which falls through to LoginPage here for signed-
    // out users (and to the first-board redirect for signed-in).
    if (location.pathname === '/') {
      return <MarketingPage slug="landing" isAuthed={false} signupsPaused={!signupsOpen} />;
    }
    // New sign-ups paused: the LoginPage still renders so existing users
    // (and admins) sign in normally — only the guest slot becomes a "join
    // the waitlist" action. The Turnstile waitlist shows on demand: the
    // LoginPage button (setShowWaitlist) or the landing CTA (#waitlist).
    // Fail-open: signupsOpen is true unless config/app.acceptingSignups is
    // explicitly false.
    if (!signupsOpen && (showWaitlist || location.hash === '#waitlist')) {
      return (
        <WaitlistScreen
          onBack={() => { setShowWaitlist(false); if (location.hash) navigate(location.pathname, { replace: true }); }}
        />
      );
    }
    return (
      <LoginPage
        onGoogleSignIn={signInWithGoogle}
        onGoogleIdTokenSignIn={signInWithGoogleIdToken}
        onGuestSignIn={signInAsGuest}
        onMagicLinkRequest={sendMagicLink}
        signupsPaused={!signupsOpen}
        onJoinWaitlist={() => setShowWaitlist(true)}
      />
    );
  }
  if (boardsLoading) return <Loading text="loading boards..." />;

  // Welcome-setup popup intercepts the route layer when the user has no
  // boards yet. Once they confirm, addBoardFromTemplate creates the
  // selected boards and we navigate to the priority destination — so the
  // popup and the routes effectively never coexist.
  // Same suppress condition as the useEffect above: skip welcome if the
  // user has shared boards, AND skip whenever the URL declares specific
  // intent. Belt-and-suspenders against any state-timing edge case where
  // showWelcome is already true when we arrive on a `/list/:id` path.
  const hasSharedContentForGate = Array.isArray(sharedWithMe) && sharedWithMe.length > 0;
  if (showWelcome && boards.length === 0 && !hasSharedContentForGate && !pathDeclaresIntent) {
    return <WelcomeSetup onConfirm={handleWelcomeConfirm} />;
  }

  return (
    <>
      <Routes>
        <Route
          path="/board/:boardId"
          element={
            <BoardRoute
              user={user}
              boards={boards}
              userPrefs={userPrefs}
              updateUserPrefs={updateUserPrefs}
              reminders={reminders}
              createReminder={createReminder}
              updateReminder={updateReminder}
              deleteReminder={deleteReminder}
              pushSub={pushSub}
              listIdToBoardId={listIdToBoardId}
              sharedWithMe={sharedWithMe}
              sharedListPlacements={sharedListPlacements}
              sharedListPlacementsByBoard={sharedListPlacementsByBoard}
              setSharedListPlacement={setSharedListPlacement}
              clearSharedListPlacement={clearSharedListPlacement}
              onCreateBlankBoard={handleCreateBlankBoard}
              onSignOut={signOut}
              onLinkGoogle={linkToGoogle}
              onLinkMagicLink={linkToMagicLink}
              onSwitchBoard={handleSwitchBoard}
              onNewBoard={handleNewBoard}
              onRenameBoard={handleRenameBoard}
              onDeleteBoard={handleDeleteBoard}
              onUpdateBoard={handleUpdateBoard}
            />
          }
        />
        <Route
          path="/shared-with-me"
          element={
            <SharedWithMePage
              sharedWithMe={sharedWithMe}
              user={user}
              onSignOut={signOut}
              boards={boards}
              onNewBoard={handleNewBoard}
            />
          }
        />
        <Route
          path="/list/:listId"
          element={<ListPage user={user} reminders={reminders} />}
        />
        {/* Reminder edit/snooze landing page (wishlist #4a). Linked
            from notification emails — lets the recipient push a
            reminder out by an hour/day or pick an exact new time
            without leaving the inbox to find it in the app. The
            page handles its own sign-in widget so a logged-out
            user clicking from email can complete the action in
            one round-trip. */}
        <Route
          path="/reminders/:reminderId/edit"
          element={<RemindersEditPage />}
        />
        {/* Trash + restore (SHARING_SPEC §6). Owner sees their own
            trash collection. Soft-deleted boards/lists land here for
            30 days; daily expireTrashItems sweep hard-deletes past
            TTL. */}
        <Route path="/trash" element={<TrashPage user={user} />} />
        {/* Admin operator console (ADMIN_SPEC.md). Bundle-split via
            React.lazy — the admin code only ships when this route
            is hit. Suspense fallback shows a brief loading splash
            while the chunk loads. AdminApp itself runs the role
            gate; non-admins get a generic 404 body. */}
        <Route
          path="/admin/*"
          element={
            <Suspense fallback={<Loading text="loading admin…" />}>
              <AdminApp user={user} />
            </Suspense>
          }
        />
        <Route
          path="*"
          element={(() => {
            if (boards.length === 0) {
              if (Array.isArray(sharedWithMe) && sharedWithMe.length > 0) {
                // No own boards, but has shared content. Land on
                // /shared-with-me so the user can pick which shared
                // board/list to open.
                return <Navigate to="/shared-with-me" replace />;
              }
              return <Loading text="loading..." />;
            }
            // Walter 2026-05-13: signed-in users at / (or any unmatched
            // path) land on their LAST viewed board, not boards[0].
            // The last-board id is written to localStorage by
            // BoardRoute on every render. Validate it's still in the
            // user's boards before honoring — the saved id might be
            // for a deleted/archived board, a different account, or
            // a different device's history.
            let target = boards[0].id;
            try {
              const saved = user?.uid ? localStorage.getItem(lastBoardKey(user.uid)) : null;
              if (saved && boards.some(b => b.id === saved)) {
                target = saved;
              }
            } catch { /* localStorage unavailable — fall back to boards[0] */ }
            // Walter 2026-05-14: preserve query string on redirect so
            // ?agenda=1 (digest email link) flows through to BoardPage
            // and auto-opens the agenda panel. Without this, the
            // ?agenda=1 on / gets dropped at the catch-all redirect
            // and the user lands on their board with no panel open.
            return <Navigate to={`/board/${target}${location.search || ''}`} replace />;
          })()}
        />
      </Routes>
      <NewBoardModal
        open={showNewBoard}
        onClose={() => setShowNewBoard(false)}
        onCreate={handleNewBoardCreate}
      />
    </>
  );
}

// Walter 2026-05-13: per-user localStorage key for the last-viewed
// board so the / catch-all can land signed-in users on the board
// they were on instead of always falling back to boards[0]. Keyed
// by uid so multiple accounts on one device don't clobber each
// other's last-board.
const lastBoardKey = (uid) => `lists_last_board_${uid || 'anon'}`;

function BoardRoute({ user, boards, userPrefs, updateUserPrefs, reminders, createReminder, updateReminder, deleteReminder, pushSub, listIdToBoardId, sharedWithMe, sharedListPlacements, sharedListPlacementsByBoard, setSharedListPlacement, clearSharedListPlacement, onCreateBlankBoard, onSignOut, onLinkGoogle, onLinkMagicLink, onSwitchBoard, onNewBoard, onRenameBoard, onDeleteBoard, onUpdateBoard }) {
  const { boardId } = useParams();
  // Stamp the last-viewed-board on every successful render so the
  // catch-all can read it. Conditioned on boardId itself so we don't
  // overwrite during the brief loading/redirect phase before
  // resolved.board exists.
  useEffect(() => {
    if (!user?.uid || !boardId) return;
    try { localStorage.setItem(lastBoardKey(user.uid), boardId); } catch {}
  }, [user?.uid, boardId]);
  // Resolve the boardId to a concrete board doc — first checking the
  // user's own boards, then falling back to a cross-user collectionGroup
  // query for shared boards. Always run the resolver: the alpha flag
  // gates the SHARE UI (only opted-in users can invite), but anyone
  // who's been added to a shared board's editors/viewers must be able
  // to navigate to it regardless of their own alpha setting. Otherwise
  // an invitee who hasn't yet flipped the flag would silently bounce.
  const resolved = useResolvedBoard({
    boardId,
    currentUid: user?.uid,
    ownBoards: boards,
  });

  // No board resolved — handle in priority order:
  //   - Still resolving: show loading splash.
  //   - Resolution done, user has other boards: redirect to first one
  //     (typed URL didn't match anything they have access to).
  //   - Resolution done, user has no boards: redirect to "/", which
  //     falls through to /shared-with-me or to "loading..." while
  //     boards sync. Critical for the post-WelcomeSetup race: when
  //     the user just created boards and we navigated here, the
  //     local boards subscription may not yet have the new docs.
  //     The "/" route re-evaluates as soon as boards sync, so we
  //     end up at the right place without ever showing the
  //     misleading "No board selected" dead-end of the old fallback.
  if (!resolved.board) {
    if (resolved.loading) {
      return <Loading text="opening board..." />;
    }
    if (boards.length > 0) {
      try {
        if (resolved.error === 'no-access' && boardId) {
          sessionStorage.setItem(
            'lists_redirect_reason',
            JSON.stringify({
              kind: 'no-access',
              attemptedBoardId: boardId,
              at: Date.now(),
            }),
          );
        }
      } catch {}
      return <Navigate to={`/board/${boards[0].id}`} replace />;
    }
    // boards is still [] — let "/" route decide based on the latest
    // boards + sharedWithMe state (which may have just synced).
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {/* Walter 2026-05-28 (#8c): site-wide guest-upgrade banner.
          Self-filters on user.isAnonymous + trigger thresholds (3 lists
          OR 1 reminder OR 2 days since account creation). Snooze/permanent
          dismiss state in localStorage so anonymous data clear → fresh
          prompts on the new guest account. onSignIn navigates to /login
          where the user can pick magic-link / Google / continue-as-guest;
          the guest path lands them back here with the same anonymous
          uid, so we don't lose work. */}
      <GuestUpgradeHint
        user={user}
        listsCount={boards.length}
        remindersCount={Array.isArray(reminders) ? reminders.length : 0}
        // Walter 2026-06-13 (s3): the old navigate('/login') did nothing for a
        // guest — an anonymous user is already "authenticated", so /login just
        // renders the board. Route the banner's sign-in to the SAME account
        // surface the in-app sign-in prompts use (account popover on desktop,
        // Settings→account on mobile) via a window event the board listens for.
        onSignIn={() => window.dispatchEvent(new CustomEvent('listicity:request-account'))}
      />
    <BoardPage
      user={user}
      board={resolved.board}
      // Hand BoardPage the actual owner uid so all sub-collection
      // reads/writes (lists, etc.) point at the right path. For own
      // boards this equals user.uid; for shared boards it's the
      // inviter's uid.
      boardOwnerUid={resolved.ownerUid || user?.uid}
      // True when the current user is NOT the board's owner (i.e.
      // they're a guest editor/viewer). BoardPage uses this to hide
      // owner-only affordances (delete board, rename, ACL edits).
      isGuestOnBoard={resolved.ownerUid && resolved.ownerUid !== user?.uid}
      boards={boards}
      userPrefs={userPrefs}
      updateUserPrefs={updateUserPrefs}
      reminders={reminders}
      createReminder={createReminder}
      updateReminder={updateReminder}
      deleteReminder={deleteReminder}
      pushSub={pushSub}
      listIdToBoardId={listIdToBoardId}
      sharedWithMe={sharedWithMe}
      sharedListPlacements={sharedListPlacements}
      sharedListPlacementsByBoard={sharedListPlacementsByBoard}
      setSharedListPlacement={setSharedListPlacement}
      clearSharedListPlacement={clearSharedListPlacement}
      onCreateBlankBoard={onCreateBlankBoard}
      onSignOut={onSignOut}
      onLinkGoogle={onLinkGoogle}
      onLinkMagicLink={onLinkMagicLink}
      onSwitchBoard={onSwitchBoard}
      onNewBoard={onNewBoard}
      onRenameBoard={onRenameBoard}
      onDeleteBoard={onDeleteBoard}
      onUpdateBoard={onUpdateBoard}
    />
    </>
  );
}

function Loading({ text }) {
  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--bg)',
      color: 'var(--text-dimmer)', fontFamily: 'var(--mono)', fontSize: '12px'
    }}>
      {text}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      {/* Walter 2026-06-13: top-level error boundary. Before this, any
          render crash white-screened the whole app with no recovery (e.g.
          the Sidebar #300 hooks-mismatch on an anonymous→real sign-in while
          Firestore was blocked). Now a crash shows a recovery card with a
          reload + ad-blocker-whitelist guidance. */}
      <AppErrorBoundary>
        <AppInner />
      </AppErrorBoundary>
      <InstallHint />
      {/* Walter 2026-05-28: proactive detection for ad-blocker extensions
          that block Firestore's WebChannel transport. Self-contained —
          runs a one-shot fetch probe on first mount, shows a soft top
          banner only when detection fires. Suppressed on marketing
          pages. See components/ExtensionBlockHint.js. */}
      <ExtensionBlockHint />
      {/* Walter 2026-08-09: tells a long-lived tab it's running stale code.
          index.js's auto-update hangs off the service worker's `updatefound`,
          but /reminder-sw.js is a static file — an ordinary deploy leaves it
          byte-identical, so that path never fires. This one compares the
          running bundle against asset-manifest.json, which changes every
          deploy. See components/NewVersionHint.js for the full story (his PC
          sat a day behind and produced a two-device divergence). */}
      <NewVersionHint />
      {/* Walter 2026-09-06: the other direction. NewVersionHint catches a tab
          running stale CODE; this one catches a tab running stale DATA — a
          window whose Firestore client has died, which keeps saving happily
          over the API fallback while receiving nothing. His PWA window did
          exactly that for 22 hours behind a green "synced". See
          components/StaleConnectionBanner.js and lib/clientHealth.js. */}
      <StaleConnectionBanner />
    </BrowserRouter>
  );
}
